import { Component, Input, OnChanges, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import * as X2JS from 'x2js';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExtractService } from '../services/extract.service';
import { ItemService, InfoService, StreamService, UserService } from '../services';
import { map, flatMap, tap } from 'rxjs/operators';
import { uuid } from 'uuidv4';

@Component({
    selector: 'item-editor',
    template: `
        <div fxFlex="824px" fxFlex.lt-md="100">
            <mat-toolbar>
                <h3 style="margin-top: 0px; margin-bottom: 0px;">{{item ? 'Edit' : 'Post'}} Item</h3>
                <span fxFlex></span>
                <button mat-raised-button color="primary" 
                    [disabled]="form.disabled || form.invalid" 
                    (click)="post()">{{ item ? 'Save' : 'Post' }}</button>
                <button mat-icon-button (click)="finished.emit()">
                    <mat-icon>clear</mat-icon>
                </button>
            </mat-toolbar>
            <mat-card [formGroup]="form">
                <mat-card-content>
                    <div fxFlex *ngIf="noTouching" fxLayoutAlign="center center">
                        <spinner></spinner>
                    </div>
                    <div fxFlex *ngIf="!noTouching" fxLayout="column">
                        <div fxFlex fxFlexLayout="row">
                            <mat-form-field fxFlex="50">
                                <input matInput placeholder="Source URL" formControlName="link">
                                <mat-error *ngIf="form.get('link').hasError('pattern')">Url must be valid to import.</mat-error>
                            </mat-form-field>
                            <button mat-button 
                                    (click)="importArticle()" 
                                    [disabled]="form.get('link').invalid || !form.get('link').value"
                                    style="padding-left: 15px;">Import Article</button>
                        </div>
                        <div fxFlex fxFlexLayout="row">
                            <mat-form-field fxFlex="50">
                                <input matInput placeholder="Title" formControlName="title">
                                <mat-error *ngIf="form.get('title').hasError('required')">Title cannot be empty.</mat-error>
                            </mat-form-field>
                        </div>
                        <div fxFlex fxFlexLayout="row">
                            <mat-form-field fxFlex="50">
                                <input matInput placeholder="Author" formControlName="author">
                                <mat-error *ngIf="form.get('author').hasError('required')">Author cannot be empty.</mat-error>
                            </mat-form-field>
                        </div>
                        <div fxFlex fxFlexLayout="row">
                            <mat-label style="color: rgba(0,0,0,0.54); padding-bottom: 5px;">Content</mat-label>
                        </div>
                        <div fxFlex fxFlexLayout="row">
                            <attensa-editor fxFlex formControlName="defaultDescription" (editorPlainTextChanged)="editorPlainTextChanged($event)"></attensa-editor>
                        </div>
                        <div fxFlex fxFlexLayout="row">
                            <mat-error *ngIf="form.get('defaultDescription').touched && form.get('defaultDescription').hasError('required')" fxFlex="90">
                                Content cannot be empty.
                            </mat-error>   
                        </div>
                        <!--
                        <div fxFlex fxFlexLayout="row">
                            <mat-form-field>
                                <ngx-mat-file-input [formControl]="attachmentsControl"
                                                    #attachments
                                                    placeholder="Attachments" 
                                                    multiple></ngx-mat-file-input>
                                <button mat-icon-button matSuffix 
                                        *ngIf="!attachments.empty" 
                                        (click)="attachments.clear($event)">
                                    <mat-icon>clear</mat-icon>
                                </button>
                                <mat-icon matSuffix>folder</mat-icon>
                            </mat-form-field>
                        </div>
                        -->
                    </div>
                </mat-card-content>
                <mat-card-actions>
                    <span fxFlex></span>
                    <button mat-raised-button color="primary" 
                            [disabled]="form.disabled || form.invalid" 
                            (click)="post()">{{item ? 'Save' : 'Post'}}</button>
                    <button style="margin-left: 5px" mat-raised-button (click)="finished.emit()">Cancel</button>
                </mat-card-actions>
            </mat-card>
        </div>
    `
})
export class ItemEditorComponent implements OnChanges {
    @Input() topicId: string;
    @Input() itemId: string;
    @Input() sourceItemId: string;
    @Output() finished = new EventEmitter<any>();
    form: FormGroup;
    attachmentsControl: FormControl;
    readonly urlPattern = /\b((http|https):\/\/?)[^\s()<>]+(?:\([\w\d]+\)|([^[:punct:]\s]|\/?))/;
    x2js = new X2JS();
    noTouching: boolean;
    item: any;
    itemPlainText: any;

    constructor(private fb: FormBuilder,
                private snackbar: MatSnackBar,
                private extractService: ExtractService,
                private itemService: ItemService,
                private infoService: InfoService,
                private streamService: StreamService,
                private userService: UserService) {}
    
        ngOnChanges(changes: SimpleChanges): void {
            if (!this.form) {
                const user = this.userService.getCurrentUser();
                this.form = this.fb.group({
                    title: ['', { validators: Validators.required }],
                    author: [user.firstName + ' ' + user.lastName, { validators: Validators.required }],
                    type: ['MANUAL'],
                    link: ['', { validators: Validators.pattern(this.urlPattern) }],
                    copiedFromStreamId: [],
                    defaultDescription: ['', { validators: Validators.required }]
                });

                this.attachmentsControl = this.fb.control('');
            }
            if (changes.itemId?.currentValue) {
                this.itemService.getItem(this.itemId)
                .pipe(map(itemRes => itemRes.item))
                .subscribe(item => {
                    this.item = item;
                    this.form.patchValue(item);
                    if (item.link === '#') {
                        this.form.get('link').setValue('');
                    }
                    this.form.controls.link.markAsTouched();
                    this.form.controls.link.setValidators([Validators.pattern(this.urlPattern)]);
                });
            }
            if (changes.sourceItemId?.currentValue) {
                this.itemService.getItem(this.sourceItemId)
                .pipe(map(itemRes => itemRes.item))
                .subscribe(item => {
                    this.form.patchValue(item);
                    if (item.link === '#') {
                        this.form.controls.link.setValue('');
                    }
                    this.form.controls.type.setValue('COPY');
                    this.form.controls.copiedFromStreamId.setValue(item.streamId);
                    
                    // extreme paranoia here to try and accomodate 
                    // chromium edge related random issues
                    setTimeout(() => { 
                        this.form.controls.link.markAsTouched();
                        this.form.controls.link.setValidators([Validators.pattern(this.urlPattern)]);
                        this.form.controls.link.updateValueAndValidity();
                        this.form.controls.link.markAsTouched();
                        this.form.updateValueAndValidity();
                    }, 1000);
                });
            }
        }

        uploadFiles(files = []) {
            return forkJoin(files.map(file => this.infoService.getS3Creds(file.name, file.type)))
            .pipe(
                flatMap(s3CredsSet => 
                    forkJoin(s3CredsSet.map((s3Creds, index) => 
                        this.infoService.uploadFile(files[index], s3Creds)))
                ),
                map(results => results.map((result, index) => {
                    const location = this.x2js.xml2js(result).PostResponse.Location;
                    const file = files[index];
                    return { id: uuid(), location, fileName: file.name, sizeInBytes: file.size, success: true, mimeType: file.type };
                }))
            );
        }

        editorPlainTextChanged(plainText) {
            this.itemPlainText = plainText;
        }

        post() {
            if (this.item) {
                this.itemService.patchItem(this.item.streamId, this.item.id, this.form.value)
                .pipe(
                    tap(() => this.snackbar.open(`Item received and is being processed.`, 'OK', { duration: 5000 })),
                    tap(() => this.itemService.itemContextChanged.next({ itemId: this.item.id, streamId: this.topicId }))
                )
                .subscribe(() => {
                    const shortDescription = (this.itemPlainText || '').substring(0, 200);
                    Object.assign(this.item, this.form.value, { shortDescription });
                    this.finished.emit(this.item);
                });
            } else {
                const files = this.attachmentsControl.value.files;
                (files ? this.uploadFiles(files) : of(undefined))
                .pipe(
                    flatMap(attachments => {
                        const item = this.form.value;
                        item.attachments = attachments;
                        // prevent tiny issues from trashing the flow
                        try { this.form.disable(); } catch (e) {  }
                        return this.streamService.saveItem(this.topicId, item);
                    }),
                    tap(() => this.finished.emit(this.item)),
                    tap(() => this.snackbar.open(`Item received and is being processed.`, 'OK', { duration: 5000 }).afterDismissed()),
                    tap(() => this.itemService.itemContextChanged.next())
                ).subscribe(); 
            }
        }

        importArticle() {
            this.noTouching = true;
            this.extractService.getUrlDetails(this.form.get('link').value)
            .pipe(
                map(results => results.objects[0]),
                tap(() => this.noTouching = false)
            )
            .subscribe(articleDetail => {
                this.form.get('title').setValue(articleDetail.title);
                this.form.get('defaultDescription').setValue(articleDetail.html);
                if (articleDetail.author && articleDetail.author.length !== 0) {
                    this.form.get('author').setValue(articleDetail.author);
                }
                this.form.controls.link.updateValueAndValidity();
            });
        }
}
